import {
  InvoiceFileNameEnum,
  ServiceTypeEnum,
  StatementFileNameEnum,
} from '@sr-sdks/api-sdk-axios';

/** The file-name enum each download endpoint accepts. */
export interface StatementFileNameBySource {
  invoice: InvoiceFileNameEnum;
  statement: StatementFileNameEnum;
}

export type StatementFileSource = keyof StatementFileNameBySource;

export type StatementFileName = StatementFileNameBySource[StatementFileSource];

/** What a download needs from its row to name the saved file. */
export interface StatementDownloadRow {
  _id: string;
  referenceNo?: string;
}

/**
 * Royalty files take their UI label (ADR-APP-015): Title Statement on a
 * Statement, Earnings Statement on an Invoice. The disbursement PDF has one
 * name on both.
 */
export const STATEMENT_FILE_NAMES: {
  [S in StatementFileSource]: Record<
    'csv' | 'disbursementPdf' | 'pdf',
    StatementFileNameBySource[S]
  >;
} = {
  invoice: {
    csv: InvoiceFileNameEnum.EarningsStatementCsv,
    disbursementPdf: InvoiceFileNameEnum.DisbursementStatementPdf,
    pdf: InvoiceFileNameEnum.EarningsStatementPdf,
  },
  statement: {
    csv: StatementFileNameEnum.TitleStatementCsv,
    disbursementPdf: StatementFileNameEnum.DisbursementStatementPdf,
    pdf: StatementFileNameEnum.TitleStatementPdf,
  },
};

interface StatementFile<TFileName extends string> {
  available: boolean;
  fileName: TFileName;
  /** What the browser saves the download as. */
  savedFileName: string;
}

export interface StatementFiles<TFileName extends string> {
  csv: StatementFile<TFileName> | null;
  pdf: StatementFile<TFileName>;
}

export interface StatementFileFlags {
  hasDisbursementStatementPdf?: boolean;
  hasRoyaltyStatementCsv?: boolean;
  hasRoyaltyStatementPdf?: boolean;
  serviceType: ServiceTypeEnum;
}

/**
 * Disbursement downloads are outside ADR-APP-015, so they keep the name they
 * were saved under before it.
 */
export const toDisbursementSavedFileName = (
  entityId: string,
  fileName: StatementFileName,
) => `Payment-Statement-${entityId}-${fileName}`;

export const usesDisbursementPdfOnly = (
  serviceType: ServiceTypeEnum,
): boolean =>
  serviceType === ServiceTypeEnum.Disbursements ||
  serviceType === ServiceTypeEnum.CollectionAccount ||
  serviceType === ServiceTypeEnum.Residuals;

/**
 * Derives the correct statement file name(s) and availability for a row.
 *
 * Rows for `Disbursements`, `CollectionAccount`, and `Residuals` service
 * types have no CSV export (csv is null) and use the disbursement PDF flag
 * and file name. These three service types are Invoice-backed (served by
 * GET /invoice per packages/backend/docs/payments-microservice.md:274) and
 * all use `hasDisbursementStatementPdf` flag with `DisbursementStatementPdf`
 * file name.
 *
 * All other service types (`RoyaltiesAnz`, `RoyaltiesWorld`) get the royalty
 * PDF/CSV pair. Both entities carry the same `hasDisbursementStatementPdf` /
 * `hasRoyaltyStatementPdf` / `hasRoyaltyStatementCsv` flags, but their royalty
 * file names differ, so `source` picks the names the row's API serves.
 */
export const getStatementFiles = <TSource extends StatementFileSource>(
  row: StatementFileFlags & StatementDownloadRow,
  source: TSource,
): StatementFiles<StatementFileNameBySource[TSource]> => {
  const fileNames = STATEMENT_FILE_NAMES[source];

  // Disbursements, CollectionAccount, and Residuals use the disbursement PDF flag
  if (usesDisbursementPdfOnly(row.serviceType)) {
    return {
      csv: null,
      pdf: {
        available: !!row.hasDisbursementStatementPdf,
        fileName: fileNames.disbursementPdf,
        savedFileName: toDisbursementSavedFileName(
          row._id,
          fileNames.disbursementPdf,
        ),
      },
    };
  }

  /*
   * ADR-APP-015: `{referenceNo}-{fileName}` lets a member match a download to
   * its row. `||`, not `??`, so an empty reference also falls back to the id.
   */
  const savedPrefix = row.referenceNo || row._id;

  // All other service types (RoyaltiesAnz, RoyaltiesWorld) get the royalty pair
  return {
    csv: {
      available: !!row.hasRoyaltyStatementCsv,
      fileName: fileNames.csv,
      savedFileName: `${savedPrefix}-${fileNames.csv}`,
    },
    pdf: {
      available: !!row.hasRoyaltyStatementPdf,
      fileName: fileNames.pdf,
      savedFileName: `${savedPrefix}-${fileNames.pdf}`,
    },
  };
};
